/**
 * Route table (data router). Path scheme, kept in sync with lib/jobs.ts
 * `routes` and the server's SearchHit.route:
 *
 *   /                                  -> all-token overview
 *   /login
 *   /t/:chain/:token                   overview (overview_tape)
 *   /t/:chain/:token/tape              (overview_tape)
 *   /t/:chain/:token/family/:address   (family)
 *   /t/:chain/:token/holders           (holders_pnl)
 *   /t/:chain/:token/entry             (holders_pnl)  entry · supply · pnl
 *   /t/:chain/:token/flow              (holders_pnl)
 *   /t/:chain/:token/liquidity[/:tab]  (liquidity_hooks_signals)  tab: book | history | lps
 *   /liquidity/:chain                  ledgers (liquidity_hooks_signals)
 *   /liquidity/:chain/ledger/:contract
 *   /hooks/:chain, /hooks/:chain/hook/:hook
 *   /signals, /alerts, /positions, /health, /compare
 *
 * Every view module is loaded lazily (see views/overview_tape/routes): the
 * shell, the login page and the home overview must not pull the chart
 * libraries into the entry chunk.
 */
import { createBrowserRouter, Outlet } from "react-router";
import { AppShell } from "@/layout/AppShell";
import { EmptyState } from "@/components/EmptyState";
import HomeView from "@/views/HomeView";
import NotFoundView from "@/views/NotFoundView";
import LoginView from "@/views/login/LoginView";
import { familyRoutes } from "@/views/family/routes";
import { holdersPnlRoutes } from "@/views/holders_pnl/routes";
import { chainAndGlobalRoutes, tokenLiquidityRoutes } from "@/views/liquidity_hooks_signals/routes";
import { overviewTapeRoutes } from "@/views/overview_tape/routes";
import { alertsRoutes } from "@/views/alerts/routes";
import { positionsRoutes } from "@/views/positions/routes";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginView /> },
  {
    path: "/",
    element: <AppShell />,
    errorElement: <NotFoundView />,
    // shown while the first lazy route module is fetched
    hydrateFallbackElement: <EmptyState title="loading…" />,
    children: [
      { index: true, element: <HomeView /> },
      {
        path: "t/:chain/:token",
        element: <Outlet />,
        children: [...overviewTapeRoutes, ...familyRoutes, ...holdersPnlRoutes, ...tokenLiquidityRoutes],
      },
      ...chainAndGlobalRoutes,
      ...alertsRoutes,
      ...positionsRoutes,
      {
        path: "compare",
        lazy: async () => ({ Component: (await import("@/views/compare/CompareView")).default }),
      },
      { path: "*", element: <NotFoundView /> },
    ],
  },
]);
